import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post, put } from 'utils/api';
import { logError } from 'utils/log';

import type { Dispatch, ThunkAction, TypedAction } from 'types';
import type { Query, QueryState } from 'modules/insights/reports/types';

const { INSIGHTS_REPORTS } = API.END_POINTS;

const QUERIES_PROCESSING = 'insights-reports/QUERIES_SINGLE_PROCESSING';
const QUERIES_LOADED = 'insights-reports/QUERIES_SINGLE_LOADED';
const QUERIES_CREATED = 'insights-reports/QUERIES_SINGLE_CREATED';
const QUERIES_UPDATED = 'insights-reports/QUERIES_SINGLE_UPDATED';
const QUERIES_DELETED = 'insights-reports/QUERIES_SINGLE_DELETED';
const QUERIES_RESET = 'insights-reports/QUERIES_SINGLE_RESET';

const initialState: QueryState = {
  queries: [],
  query: {},
  isLoading: true
};

type Status = {
  status: boolean
};

const setStatus = (
  status: boolean
): TypedAction<Status> => ({
  type: QUERIES_PROCESSING,
  payload: {
    status
  }
});

type Queries = {
  queries: Array<Query>
};

const queriesLoaded = (
  queries: Array<Query>
): TypedAction<Queries> => ({
  type: QUERIES_LOADED,
  payload: {
    queries
  }
});

type QuerySingle = {
  query: Query
};

const queryCreated = (
  query: Query
): TypedAction<QuerySingle> => ({
  type: QUERIES_CREATED,
  payload: {
    query
  }
});

type QueryWithId = {
  query: Query,
  queryId: number | null | undefined
};

const queryUpdated = (
  query: Query,
  queryId?: number | null
): TypedAction<QueryWithId> => ({
  type: QUERIES_UPDATED,
  payload: {
    query,
    queryId
  }
});

type QueryId = {
  queryId: number
}

const queryDeleted = (
  queryId: number
): TypedAction<QueryId> => ({
  type: QUERIES_DELETED,
  payload: {
    queryId
  }
});

type Reset = {
  type: typeof QUERIES_RESET
};

export const resetQueries = (): TypedAction<Reset> => ({
  type: QUERIES_RESET
});

export const fetchQueries = (
  reportId: number
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(setStatus(true));

    try {
      const url = INSIGHTS_REPORTS.fetchQueries(reportId);
      const response = await get(url);
      const queries = response.data || [];

      dispatch(setStatus(false));

      dispatch(queriesLoaded(queries));
    } catch (error) {
      logError(error);
    } finally {
      dispatch(setStatus(false));
    }
  }
);

export const createQuery = (
  query: Query
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(setStatus(true));

    try {
      const url = INSIGHTS_REPORTS.createQuery(query.reportId);
      const response = await post(url, { data: query });
      const responseData = response.data || {};

      dispatch(setStatus(false));

      return dispatch(queryCreated(responseData));
    } catch (error) {
      return logError(error);
    } finally {
      dispatch(setStatus(false));
    }
  }
);

export const updateQuery = (
  query: Query
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(setStatus(true));

    const queryId = query.id;

    try {
      const url = INSIGHTS_REPORTS.updateQuery(query.reportId, queryId);
      const response = await put(url, { data: query });
      const responseData = response.data || {};

      dispatch(setStatus(false));

      return dispatch(queryUpdated(responseData, queryId));
    } catch (error) {
      return logError(error);
    } finally {
      dispatch(setStatus(false));
    }
  }
);

export const duplicateQuery = (
  reportId: number,
  queryId: number
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(setStatus(true));

    try {
      const url = INSIGHTS_REPORTS.cloneQuery(reportId, queryId);
      const response = await post(url, { data: {} });
      const responseData = response.data || {};

      dispatch(setStatus(false));

      return dispatch(queryCreated(responseData));
    } catch (error) {
      return logError(error);
    } finally {
      dispatch(setStatus(false));
    }
  }
);

export const deleteQuery = (
  reportId: number,
  queryId: number
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(setStatus(true));

    try {
      const url = INSIGHTS_REPORTS.deleteQuery(reportId, queryId);

      await destroy(url);

      dispatch(setStatus(false));

      dispatch(queryDeleted(queryId));
    } catch (error) {
      logError(error);
    } finally {
      dispatch(setStatus(false));
    }
  }
);

const handleProcessing = (
  state: QueryState,
  { status }: Status
): QueryState => ({
  ...state,
  isLoading: status
});

const handleLoaded = (
  state: QueryState,
  { queries }: Queries
): QueryState => ({
  ...state,
  queries,
  isLoading: false
});

const handleCreated = (
  state: QueryState,
  { query }: QuerySingle
): QueryState => ({
  ...state,
  query,
  queries: [
    query,
    ...state.queries
  ]
});

const handleUpdated = (
  state: QueryState,
  { query, queryId }: QueryWithId
): QueryState => ({
  ...state,
  query,
  queries: [
    ...state.queries.map(item => (
      item.id === queryId ? query : item
    ))
  ]
});

const handleDeleted = (
  state: QueryState,
  { queryId }: QueryId
): QueryState => ({
  ...state,
  queries: state.queries.filter(query => query.id !== queryId)
});

export default createReducer<QueryState, any>('insightsReportsQueries', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [QUERIES_RESET]: () => initialState,
  [QUERIES_PROCESSING]: handleProcessing,
  [QUERIES_LOADED]: handleLoaded,
  [QUERIES_CREATED]: handleCreated,
  [QUERIES_UPDATED]: handleUpdated,
  [QUERIES_DELETED]: handleDeleted
});
